import { useCallback, useEffect, useState } from 'react';
import { List } from '@refinedev/antd';
import { Button, Tag, Typography, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router';
import {
  DataTable,
  RowActionsMenu,
  useUrlTableState,
  type DataColumn,
  type TableState,
} from '@viq-admin/ui';
import { listQuestions, deleteQuestion } from '../../api/discovery';
import { DiscoveryQuestion } from '../../api/types';

export function typeLabel(q: DiscoveryQuestion): string {
  if (q.type === 'free_text') return 'Free-text';
  return q.allowMultiple ? 'Choice · multi' : 'Choice · single';
}

function answersPreview(q: DiscoveryQuestion): string {
  if (q.type === 'free_text') return '—';
  if (q.options.length <= 4) return q.options.map((o) => o.text).join(' · ');
  return `${q.options.length} options`;
}

/**
 * Seeds the default sort when the URL carries none. Module-level so its
 * identity is stable across renders — see DataTable's `defaultState` JSDoc.
 */
const DEFAULT_STATE: Partial<TableState> = {
  sort: { field: 'text', order: 'ascend' },
};

/**
 * Portal counterpart to the console's Questions Inventory list
 * (apps/console/src/resources/questions/list.tsx) — same DataTable column
 * template, so the two apps present and behave identically.
 *
 * Unlike the console, the portal is single-tenant/token-scoped: there is no
 * "operate as tenant" concept here, so this list has no tenant-selection
 * branch and fetches unconditionally. The data fetch is hand-rolled
 * (`listQuestions`/`deleteQuestion` against `../../api/discovery`) rather than
 * Refine's `useTable`, so `dataSource`/`loading` are passed to `DataTable`
 * directly instead of via `tableProps`.
 *
 * Row click opens the read-first detail page (`/questions/:id`); Edit is an
 * in-place toggle there, gated on `manage:DiscoveryQuestion` — mirrors the
 * console list's row-click wiring.
 */
export function QuestionList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<DiscoveryQuestion[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listQuestions());
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useUrlTableState(searchParams, setSearchParams);

  const defaultCount = rows.filter((r) => r.isDefault).length;

  const columns: DataColumn<DiscoveryQuestion>[] = [
    {
      dataIndex: 'text',
      key: 'text',
      title: 'Question',
      kind: 'text',
      width: 360,
      render: (t: string) => <b>{t}</b>,
    },
    {
      key: 'type',
      title: 'Type',
      kind: 'enum',
      width: 140,
      projectValue: (r) => typeLabel(r),
      render: (_: unknown, r: DiscoveryQuestion) => (
        <Tag color={r.type === 'free_text' ? 'purple' : 'blue'}>{typeLabel(r)}</Tag>
      ),
    },
    {
      key: 'answers',
      title: 'Answers',
      width: 220,
      filterable: false,
      // Escape hatch: sorts by option count rather than the rendered preview
      // text, which `kind`-derived comparators cannot express.
      sortable: (a, b) =>
        (a.type === 'free_text' ? 0 : a.options.length) -
        (b.type === 'free_text' ? 0 : b.options.length),
      render: (_: unknown, r: DiscoveryQuestion) => (
        <Typography.Text type="secondary">{answersPreview(r)}</Typography.Text>
      ),
    },
    {
      key: 'required',
      title: 'Required',
      kind: 'enum',
      width: 120,
      projectValue: (r) => (r.required ? 'Required' : 'Optional'),
      render: (_: unknown, r: DiscoveryQuestion) =>
        r.required ? (
          <Tag color="gold">Required</Tag>
        ) : (
          <Typography.Text type="secondary">Optional</Typography.Text>
        ),
    },
    {
      key: 'default',
      title: 'Default',
      kind: 'enum',
      width: 120,
      projectValue: (r) => (r.isDefault ? 'Default' : 'Not default'),
      render: (_: unknown, r: DiscoveryQuestion) =>
        r.isDefault ? (
          <Typography.Text type="warning">★ Default</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      title: 'Updated',
      kind: 'date',
      width: 140,
    },
    {
      key: 'actions',
      title: '',
      kind: 'actions',
      render: (_: unknown, record: DiscoveryQuestion) => (
        <RowActionsMenu
          actions={[
            {
              key: 'delete',
              label: 'Delete',
              danger: true,
              cap: 'manage:DiscoveryQuestion',
              onRun: async () => {
                await deleteQuestion(record.id);
                await refresh();
              },
              confirm: {
                title: 'Delete this question?',
                okText: 'Delete',
                danger: true,
                successMessage: 'Question deleted',
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <List
      title="Questions Inventory"
      headerButtons={() => (
        <Button type="primary" onClick={() => navigate('/questions/new')}>
          + New question
        </Button>
      )}
    >
      <Typography.Paragraph type="secondary">
        Reusable question templates. Flag questions as <b>Default</b> to auto-add them to new
        conferences. <Tag>{defaultCount} of {rows.length} default</Tag>
      </Typography.Paragraph>
      <DataTable<DiscoveryQuestion>
        dataSource={rows}
        loading={loading}
        columns={columns}
        rowKey="id"
        storageKey="questionsList"
        state={state}
        onStateChange={setState}
        defaultState={DEFAULT_STATE}
        searchFields={['text']}
        searchPlaceholder="Search questions"
        onRowClick={(record) => navigate(`/questions/${record.id}`)}
        empty={{
          title: 'No questions yet',
          description: 'Add the first question to get started.',
        }}
      />
    </List>
  );
}
